/*
 * CONTEXTO DE NAVEGACIÓN DEL NÚCLEO · incremento 09 (D-235, D-236, D-237).
 *
 * UNA SOLA IMPLEMENTACIÓN de tres cosas que antes no existían en ninguna parte
 * (RNF-076):
 *
 *   1. El CONTEXTO DE UN LISTADO vive en la dirección, no en el estado de la
 *      pantalla (RF-147, RN-381, RN-382). Antes vivía en `useState` y por eso no
 *      existía cuando la pantalla se desmontaba: no había nada que heredar ni a
 *      dónde volver (CR-01).
 *   2. El CONTEXTO DE REGRESO viaja en la dirección del alta, en el parámetro
 *      `volverAlContexto`, para que sobreviva a un refresco y al botón atrás del
 *      teléfono (RF-145, RF-146).
 *   3. El CONTEXTO VIGENTE lo publica la pantalla de listado mientras está
 *      montada, para que el botón flotante del armazón pueda heredarlo SIN que el
 *      armazón sepa qué rutas son listados (RF-150, RN-411).
 *
 * LO QUE ESTE MÓDULO NO HACE, y cada una sería un defecto:
 *   · NO persiste nada en el dispositivo (RN-386): el contexto vive en la
 *     dirección y nada más. Abrir la aplicación en frío muestra el mes en curso.
 *   · NO sugiere NUNCA una cuenta (RF-153, RN-388, RN-433): el campo de cuenta de
 *     la captura llega vacío venga de donde venga el filtro. Lo único que se
 *     sugiere es la categoría, y solo cuando el filtro es de UNA categoría.
 *   · NO agrega llamadas al servidor (RNF-081): una dirección sin parámetros se
 *     lee exactamente como el estado inicial de hoy (RN-383).
 */

/** Parámetro con el que el alta recuerda a qué dirección exacta debe volver. */
export const PARAMETRO_DE_REGRESO = 'volverAlContexto';

/** Los NUEVE datos del contexto del historial (RN-381), en este orden. */
export const CAMPOS_DEL_CONTEXTO_DEL_HISTORIAL = Object.freeze([
  'tipo',
  'renglonesRepartidos',
  'idCuenta',
  'idCategoria',
  'texto',
  'desde',
  'hasta',
  'incluirCancelados',
  'pagina',
]);

/**
 * El valor de cada dato cuando la dirección NO lo trae (RN-383). Una dirección
 * del historial sin parámetros tiene que comportarse EXACTAMENTE como hoy: mes en
 * curso del dispositivo, sin cancelados, página 1 y sin chips.
 */
export const CONTEXTO_DEL_HISTORIAL_POR_OMISION = Object.freeze({
  tipo: '',
  renglonesRepartidos: '',
  idCuenta: '',
  idCategoria: '',
  texto: '',
  desde: '',
  hasta: '',
  incluirCancelados: false,
  pagina: 1,
});

const PATRON_DE_FECHA = /^\d{4}-\d{2}-\d{2}$/;

function aParametros(busqueda) {
  if (busqueda instanceof URLSearchParams) return busqueda;
  return new URLSearchParams(busqueda ?? '');
}

function enteroPositivo(valor) {
  if (valor === null || valor === undefined || String(valor).trim() === '') return null;
  const numero = Number(valor);
  if (!Number.isInteger(numero) || numero <= 0) return null;
  return numero;
}

function fechaValida(valor) {
  if (!valor || !PATRON_DE_FECHA.test(valor)) return '';
  // Una fecha con forma correcta pero imposible (2026-02-31) se descarta igual.
  const [anio, mes, dia] = valor.split('-').map(Number);
  const fecha = new Date(Date.UTC(anio, mes - 1, dia));
  const esReal = fecha.getUTCFullYear() === anio
    && fecha.getUTCMonth() === mes - 1
    && fecha.getUTCDate() === dia;
  return esReal ? valor : '';
}

/**
 * Lee el contexto del historial DE LA DIRECCIÓN, con la tolerancia que manda
 * RN-384: un parámetro con valor inválido SE DESCARTA y el resto se aplica. La
 * pantalla no muestra un error; muestra el listado con lo que sí era válido.
 *
 * Los catálogos se reciben por parámetro a propósito: el núcleo no conoce los
 * tipos de movimiento ni las claves de reparto, que son del módulo (frontera
 * RNF-076 / D-218).
 *
 * @param busqueda  `location.search` o un URLSearchParams.
 * @param catalogos { tipos: string[], repartos: string[] } valores admitidos.
 */
export function leerContextoDelHistorial(busqueda, catalogos = {}) {
  const parametros = aParametros(busqueda);
  const tipos = catalogos.tipos ?? [];
  const repartos = catalogos.repartos ?? [];

  const tipo = parametros.get('tipo') ?? '';
  const reparto = parametros.get('renglonesRepartidos') ?? '';
  const desde = fechaValida(parametros.get('desde'));
  const hasta = fechaValida(parametros.get('hasta'));

  // RN-384: un rango al revés se descarta COMPLETO y el resto del filtro se
  // aplica. Nunca se consulta un rango que el servidor rechazaría.
  const rangoAlRevés = desde !== '' && hasta !== '' && desde > hasta;

  const idCuenta = enteroPositivo(parametros.get('idCuenta'));
  const idCategoria = enteroPositivo(parametros.get('idCategoria'));
  const pagina = enteroPositivo(parametros.get('pagina'));
  const cancelados = parametros.get('incluirCancelados');

  return {
    tipo: tipos.includes(tipo) ? tipo : '',
    renglonesRepartidos: repartos.includes(reparto) ? reparto : '',
    idCuenta: idCuenta === null ? '' : String(idCuenta),
    idCategoria: idCategoria === null ? '' : String(idCategoria),
    texto: parametros.get('texto') ?? '',
    desde: rangoAlRevés ? '' : desde,
    hasta: rangoAlRevés ? '' : hasta,
    incluirCancelados: cancelados === '1' || cancelados === 'true',
    pagina: pagina === null ? 1 : pagina,
  };
}

/**
 * Escribe el contexto en una cadena de búsqueda. SOLO viajan los datos que se
 * apartan del valor por omisión: así la dirección del mes en curso sigue siendo
 * `/movimientos` pelado, como hoy (RN-383, RNF-081).
 *
 * @param contexto   los nueve datos.
 * @param conservar  parámetros desconocidos que ya venían en la dirección. Se
 *                   conservan porque RN-383 manda IGNORARLOS, no borrarlos.
 */
export function escribirContextoDelHistorial(contexto, conservar = null) {
  const parametros = new URLSearchParams();

  if (conservar) {
    for (const [clave, valor] of aParametros(conservar).entries()) {
      if (!CAMPOS_DEL_CONTEXTO_DEL_HISTORIAL.includes(clave)) parametros.append(clave, valor);
    }
  }

  for (const campo of CAMPOS_DEL_CONTEXTO_DEL_HISTORIAL) {
    const valor = contexto?.[campo];
    const omision = CONTEXTO_DEL_HISTORIAL_POR_OMISION[campo];

    if (campo === 'incluirCancelados') {
      if (valor === true) parametros.set(campo, '1');
      continue;
    }
    if (campo === 'pagina') {
      if (Number(valor) > 1) parametros.set(campo, String(valor));
      continue;
    }
    if (valor !== undefined && valor !== null && valor !== '' && valor !== omision) {
      parametros.set(campo, String(valor));
    }
  }

  const cadena = parametros.toString();
  return cadena === '' ? '' : `?${cadena}`;
}

/**
 * ¿Es una dirección DE ESTA APLICACIÓN? Se exige que empiece con una sola barra:
 * así `//otrositio.com` y `https://…` quedan fuera. Es lo que impide que el
 * parámetro de regreso saque a la persona de la aplicación (RN-393b).
 */
export function esRutaInterna(ruta) {
  return typeof ruta === 'string'
    && ruta.startsWith('/')
    && !ruta.startsWith('//')
    && !ruta.includes('\\');
}

/**
 * Dirección exacta a la que hay que regresar al terminar un alta (RF-146,
 * RN-390). Si no viene o no es interna, se usa el destino padre de siempre, que
 * es la conducta de hoy (RN-390c).
 */
export function leerContextoDeRegreso(busqueda, destinoPadre) {
  const crudo = aParametros(busqueda).get(PARAMETRO_DE_REGRESO);
  if (!crudo) return destinoPadre;
  const ruta = decodeURIComponent(crudo);
  return esRutaInterna(ruta) ? ruta : destinoPadre;
}

/**
 * Arma la dirección de un alta con lo que hereda de la pantalla de origen
 * (RN-387): el contexto de regreso y, aparte, los valores sugeridos.
 *
 * SON DOS COSAS SEPARADAS a propósito. La cuenta NUNCA va en `sugerencias`
 * (RF-153, RN-388): el único valor sugerido que existe es la categoría.
 */
export function construirRutaDeAlta(ruta, { contextoDeRegreso = null, sugerencias = null } = {}) {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(sugerencias ?? {})) {
    if (valor !== null && valor !== undefined && valor !== '') parametros.set(clave, String(valor));
  }

  if (contextoDeRegreso && esRutaInterna(contextoDeRegreso)) {
    parametros.set(PARAMETRO_DE_REGRESO, contextoDeRegreso);
  }

  const cadena = parametros.toString();
  return cadena === '' ? ruta : `${ruta}?${cadena}`;
}

/*
 * CONTEXTO VIGENTE · lo publica la pantalla de listado, lo lee el armazón.
 *
 * POR QUÉ NO ES UN `useState` NI UN CONTEXTO DE REACT: el armazón (`Shell`) es
 * HIJO de la pantalla, no su padre, así que no puede recibirlo por propiedad ni
 * leerlo de un proveedor que la pantalla monte por debajo. Y el armazón NO puede
 * deducirlo de la ruta sin meterse a saber qué rutas son listados, que es
 * justamente la frontera que D-218 defiende.
 *
 * Se publica EN EL RENDER y no en un efecto a propósito: los efectos del hijo
 * (`Shell`) corren ANTES que los del padre (la pantalla), así que con un efecto
 * el armazón leería el contexto de la pantalla anterior. El valor solo se lee
 * cuando la persona toca el botón flotante —nunca durante un render—, así que
 * publicarlo aquí no afecta a lo que se pinta.
 */
let contextoVigente = null;

export function publicarContextoDeListado(contexto) {
  contextoVigente = contexto ?? null;
}

export function retirarContextoDeListado(direccion) {
  if (!direccion || contextoVigente?.direccion === direccion) contextoVigente = null;
}

/** `{ direccion, sugerencias }` del listado a la vista, o null si no hay listado. */
export function obtenerContextoVigente() {
  return contextoVigente;
}
